import type { Container } from "pixi.js";
import type { ComputedGridLayout, GridLayout, GridModel, GridPoint, GridSymbolPool, PresentationTaskRunner, SymbolView, TweenEasing } from "@slot/presentation";

export type PreparedRevealTween = {
  node: Container;
  targetPosition: GridPoint;
  durationSeconds: number;
  column: number;
  row: number;
};

export type PreparedRevealPlan = {
  nextModel: GridModel;
  newSymbolsByIndex: Map<number, SymbolView>;
  tweens: PreparedRevealTween[];
  strict: boolean;
  columnStaggerSeconds: number;
  withinColumnStaggerSeconds: number;
};

/**
 * INTERNAL — the reveal drop-in: a full set of fresh views is spawned above the grid and falls into
 * place, column by column left to right, bottom row first within a column, with an overshoot-and-
 * settle bounce. Runs under the `reveal_motion` scope (the one the shell arms for turbo and slams).
 * Runner-only (engine time); with no runner it snaps to targets instantly.
 */
export class GridReveal {
  public static readonly SCOPE = "reveal_motion";

  private readonly pool: GridSymbolPool;
  private readonly layoutUtil: GridLayout;

  public constructor(input: { pool: GridSymbolPool; layoutUtil: GridLayout }) {
    this.pool = input.pool;
    this.layoutUtil = input.layoutUtil;
  }

  public prepareRevealToModel(input: {
    nextModel: GridModel;
    gridColumnCount: number;
    gridRowCount: number;
    layout: ComputedGridLayout;

    dropDurationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    spawnStartOffsetRows?: number;
    strictSymbolConsistencyCheck?: boolean;
  }): PreparedRevealPlan {
    const nextModel = input.nextModel;

    if (nextModel.columnCount !== input.gridColumnCount || nextModel.rowCount !== input.gridRowCount) {
      throw new Error(
        `GridReveal.prepareRevealToModel model size mismatch. Grid=${input.gridColumnCount}x${input.gridRowCount}, ` +
          `model=${nextModel.columnCount}x${nextModel.rowCount}`,
      );
    }

    const dropDuration = input.dropDurationSeconds ?? 0.35;
    const columnStaggerSeconds = input.columnStaggerSeconds ?? 0.06;
    const withinColumnStaggerSeconds = input.withinColumnStaggerSeconds ?? 0.02;
    const spawnOffsetRows = input.spawnStartOffsetRows ?? 3;
    const strict = input.strictSymbolConsistencyCheck ?? false;

    const newSymbolsByIndex: Map<number, SymbolView> = new Map();
    const tweens: PreparedRevealTween[] = [];

    for (let col = 0; col < input.gridColumnCount; col++) {
      for (let row = 0; row < input.gridRowCount; row++) {
        const index = this.layoutUtil.rowColToIndex(input.gridColumnCount, row, col);
        const symbolId = nextModel.cells[row][col]?.symbolId ?? "?";

        const view = this.pool.acquire();
        this.pool.applySymbolIdToView(view, symbolId);

        const targetPos = this.layoutUtil.computeLocalPosition(input.layout, row, col);
        // Spawn `spawnOffsetRows` rows ABOVE the target (Pixi y-down).
        view.node.position.set(targetPos.x, targetPos.y - input.layout.cellHeight * spawnOffsetRows);
        view.node.scale.set(1, 1);
        view.node.visible = true;

        newSymbolsByIndex.set(index, view);
        tweens.push({ node: view.node, targetPosition: targetPos, durationSeconds: dropDuration, column: col, row });
      }
    }

    return { nextModel, newSymbolsByIndex, tweens, strict, columnStaggerSeconds, withinColumnStaggerSeconds };
  }

  public async animatePreparedReveal(input: {
    plan: PreparedRevealPlan;
    runner: PresentationTaskRunner | null;
    gridColumnCount: number;
    gridRowCount: number;
    layout: ComputedGridLayout;
    strictGetSymbolIdAtIndex: (index: number) => string | null;
    /** Fired when a column's bottom row lands. */
    onColumnDropLanded?: (column: number) => void;
    /** Fired once per registered index when that symbol crosses the grid's top edge. */
    symbolEnterGridCallbacks?: Map<number, () => void>;

    enableOvershoot?: boolean;
    overshootPixels?: number;
    overshootPhaseRatio?: number;
    overshootSettleEasing?: TweenEasing;
  }): Promise<void> {
    const { plan, runner } = input;
    const scope = GridReveal.SCOPE;

    const enableOvershoot = input.enableOvershoot ?? true;
    const overshootPx = enableOvershoot ? (input.overshootPixels ?? 30) : 0;
    const overshootPhaseRatio = enableOvershoot ? (input.overshootPhaseRatio ?? 0.78) : 1;
    const settleEasing: TweenEasing = input.overshootSettleEasing ?? "quadOut";

    const tweensSorted = [...plan.tweens].sort((a, b) => {
      if (a.column !== b.column) return a.column - b.column;
      return b.row - a.row;
    });

    // No animation infra → snap everything to rest, fire every land and enter callback synchronously.
    if (!runner) {
      for (const t of tweensSorted) {
        t.node.position.set(t.targetPosition.x, t.targetPosition.y);
        input.symbolEnterGridCallbacks?.get(t.row * input.gridColumnCount + t.column)?.();
        if (t.row === input.gridRowCount - 1) input.onColumnDropLanded?.(t.column);
      }
      this.strictVerify(input);
      return;
    }

    const moveTo = async (node: Container, seconds: number, pos: GridPoint): Promise<void> => {
      const t1 = Math.max(0.01, seconds * overshootPhaseRatio);
      const t2 = Math.max(0.01, seconds * (1 - overshootPhaseRatio));
      const overshootPos = { x: pos.x, y: pos.y + overshootPx }; // below the target (Pixi y-down)
      await runner.tweenTo(node, t1, { position: overshootPos }, scope, { easing: "linear" });
      await runner.tweenTo(node, t2, { position: { x: pos.x, y: pos.y } }, scope, { easing: settleEasing });
    };

    const run = async (): Promise<void> => {
      const promises: Promise<void>[] = [];

      for (const t of tweensSorted) {
        const rowOrderWithinColumn = input.gridRowCount - 1 - t.row;
        const startDelaySeconds = t.column * plan.columnStaggerSeconds + rowOrderWithinColumn * plan.withinColumnStaggerSeconds;

        // A registered enter-grid callback fires as the symbol crosses the top edge: at the start
        // delay when it starts inside the grid, else the fraction of the overshoot phase spent above.
        const enterCallback = input.symbolEnterGridCallbacks?.get(t.row * input.gridColumnCount + t.column);
        if (enterCallback) {
          const gridTopY = input.layout.originY - input.layout.cellHeight * 0.5;
          const startY = t.node.position.y;
          let entryDelaySeconds = startDelaySeconds;
          if (startY < gridTopY) {
            const overshootY = t.targetPosition.y + overshootPx;
            const phase1Distance = overshootY - startY;
            const distToGridTop = gridTopY - startY;
            const fraction = phase1Distance > 0 ? distToGridTop / phase1Distance : 0;
            entryDelaySeconds = startDelaySeconds + fraction * (t.durationSeconds * overshootPhaseRatio);
          }
          promises.push(
            (async () => {
              if (entryDelaySeconds > 0) await runner.sleep(entryDelaySeconds, scope);
              enterCallback();
            })(),
          );
        }

        promises.push(
          (async () => {
            if (startDelaySeconds > 0) await runner.sleep(startDelaySeconds, scope);
            await moveTo(t.node, t.durationSeconds, t.targetPosition);
            if (t.row === input.gridRowCount - 1) input.onColumnDropLanded?.(t.column);
          })(),
        );
      }

      await Promise.all(promises);
    };

    await runner.runInScope(scope, run);

    this.strictVerify(input);
  }

  private strictVerify(input: { plan: PreparedRevealPlan; gridColumnCount: number; gridRowCount: number; strictGetSymbolIdAtIndex: (index: number) => string | null }): void {
    const { plan } = input;
    if (!plan.strict) return;
    for (let row = 0; row < input.gridRowCount; row++) {
      for (let col = 0; col < input.gridColumnCount; col++) {
        const index = row * input.gridColumnCount + col;
        const expectedId = plan.nextModel.cells[row][col]?.symbolId ?? "?";
        const actualId = input.strictGetSymbolIdAtIndex(index);
        if (actualId !== expectedId) {
          throw new Error(`[GridReveal] strict mismatch at row=${row} col=${col}. actual=${String(actualId)} expected=${expectedId}`);
        }
      }
    }
  }
}
